import { env } from '../../config/env'
import { clearSession, readSession, writeSession } from '../auth/session'

export type ApiErrorKind = 'unauthorized' | 'forbidden' | 'validation' | 'network' | 'timeout' | 'server' | 'unknown'

export class ApiError extends Error {
  constructor(message: string, public status: number | null, public kind: ApiErrorKind, public details?: unknown) {
    super(message)
    this.name = 'ApiError'
  }
}

let refreshPromise: Promise<boolean> | null = null

function errorKind(status: number | null): ApiErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 400 || status === 422) return 'validation'
  if (status !== null && status >= 500) return 'server'
  return status === null ? 'network' : 'unknown'
}

async function refreshSession(): Promise<boolean> {
  const session = readSession()
  if (!session?.refreshToken) return false
  try {
    const response = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    })
    if (!response.ok) return false
    const data = await response.json() as { access_token?: string; refresh_token?: string }
    if (!data.access_token || !data.refresh_token) return false
    writeSession({ accessToken: data.access_token, refreshToken: data.refresh_token })
    return true
  } catch {
    return false
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get('content-type')?.includes('application/json')
  const payload = isJson ? await response.json().catch(() => null) : await response.text().catch(() => '')
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'detail' in payload
      ? String((payload as { detail: unknown }).detail)
      : `Yêu cầu thất bại (${response.status})`
    throw new ApiError(message, response.status, errorKind(response.status), payload)
  }
  return payload as T
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  auth?: boolean
  timeoutMs?: number
  retryAuth?: boolean
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = true, timeoutMs = env.requestTimeoutMs, retryAuth = true, headers: initialHeaders, ...init } = options
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)
  const headers = new Headers(initialHeaders)
  const session = readSession()
  if (auth && session?.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`)
  const isForm = body instanceof FormData
  if (body !== undefined && !isForm && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

  try {
    const response = await fetch(`${env.apiBaseUrl}${path}`, {
      ...init,
      headers,
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      signal: options.signal ?? controller.signal,
    })
    if (response.status === 401 && auth && retryAuth) {
      refreshPromise ??= refreshSession().finally(() => { refreshPromise = null })
      const refreshed = await refreshPromise
      if (refreshed) return apiRequest<T>(path, { ...options, retryAuth: false })
      clearSession()
      if (window.location.pathname !== '/login') window.location.assign('/login')
    }
    return await parseResponse<T>(response)
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('Yêu cầu quá thời gian. Vui lòng thử lại.', null, 'timeout')
    }
    throw new ApiError('Không thể kết nối máy chủ. Kiểm tra mạng và thử lại.', null, 'network', error)
  } finally {
    window.clearTimeout(timeout)
  }
}
